import type { LiveRateMetric } from '@/domain/types'

export const LIVE_RATE_WARMUP_MS = 2 * 60_000
export const LIVE_RATE_ESTIMATE_WARMUP_MS = 30_000
export const LIVE_RATE_RECOVERY_WARMUP_MS = 30_000
export const LIVE_RATE_STALE_MS = 15_000
export const LIVE_RATE_BUCKET_MS = 10_000
export const LIVE_EFFICIENCY_WINDOW_MS = 10 * 60_000
export const LIVE_RESOURCE_WINDOW_MS = 15 * 60_000

const HOUR_MS = 3_600_000
const MINIMUM_RATE_POINTS = 12
const MINIMUM_ESTIMATE_POINTS = 4

export interface TrustedRatePoint {
  readonly trustedAt: number
  readonly cumulativeTotal: number
  readonly sourceAt: number
}

export class TrustedRateEstimator {
  private points: TrustedRatePoint[] = []
  private resetAt = 0
  private latestSourceAt: number | null = null
  private discontinuityPending = false
  private recoveryStartedAt: number | null = null
  private suspended = false
  private awaitingResumeBaseline = false
  private temporarilyUnavailable = false

  constructor(
    private readonly windowMs: number,
    private readonly warmupMs = LIVE_RATE_WARMUP_MS,
    private readonly staleMs = LIVE_RATE_STALE_MS,
    private readonly bucketMs = LIVE_RATE_BUCKET_MS,
  ) {}

  reset(at: number): void {
    this.points = []
    this.resetAt = at
    this.latestSourceAt = null
    this.discontinuityPending = false
    this.recoveryStartedAt = null
    this.suspended = false
    this.awaitingResumeBaseline = false
    this.temporarilyUnavailable = false
  }

  suspend(): void {
    this.suspended = true
    this.awaitingResumeBaseline = false
  }

  resume(): void {
    this.suspended = false
    this.awaitingResumeBaseline = true
    this.discontinuityPending = false
    this.recoveryStartedAt = null
  }

  markDiscontinuity(): void {
    if (!this.suspended) this.discontinuityPending = true
  }

  markUnavailable(): void {
    this.temporarilyUnavailable = true
    this.awaitingResumeBaseline = false
    this.discontinuityPending = false
    this.recoveryStartedAt = null
  }

  resumeFromUnavailable(): void {
    if (!this.temporarilyUnavailable) return
    this.temporarilyUnavailable = false
    this.awaitingResumeBaseline = true
    this.discontinuityPending = false
    this.recoveryStartedAt = null
  }

  observe(point: TrustedRatePoint): boolean {
    if (this.temporarilyUnavailable || !this.validPoint(point)) return false
    if (this.latestSourceAt !== null && point.sourceAt <= this.latestSourceAt) return false

    const sourceGapMs = this.latestSourceAt === null ? 0 : point.sourceAt - this.latestSourceAt
    const resumedBaseline = this.awaitingResumeBaseline
    this.awaitingResumeBaseline = false
    const recoveryRequired = sourceGapMs > this.staleMs
      && (this.discontinuityPending || this.points.length > 0)
    if (!resumedBaseline && recoveryRequired) {
      this.recoveryStartedAt = point.trustedAt
    }
    this.latestSourceAt = point.sourceAt
    this.discontinuityPending = false
    if (this.recoveryStartedAt !== null
      && point.trustedAt - this.recoveryStartedAt >= LIVE_RATE_RECOVERY_WARMUP_MS) {
      this.recoveryStartedAt = null
    }

    const previous = this.points.at(-1)
    if (previous && point.trustedAt < previous.trustedAt) return false
    const bucket = Math.floor(point.trustedAt / this.bucketMs)
    const previousBucket = previous ? Math.floor(previous.trustedAt / this.bucketMs) : -1
    if (previous && bucket === previousBucket) return true

    this.points.push(point)
    this.prune(point.trustedAt)
    return true
  }

  snapshot(now: number): LiveRateMetric {
    const latestPoint = this.points.at(-1)
    if (this.temporarilyUnavailable) {
      const anchor = this.points[0]
      return {
        value: null,
        status: 'unavailable',
        windowDurationMs: anchor && latestPoint ? Math.max(0, latestPoint.trustedAt - anchor.trustedAt) : 0,
        updatedAt: this.latestSourceAt,
      }
    }
    if (!latestPoint || this.latestSourceAt === null) {
      return {
        value: null,
        status: now - this.resetAt > this.staleMs ? 'unavailable' : 'warming',
        windowDurationMs: 0,
        updatedAt: null,
      }
    }

    const anchor = this.points[0]!
    const windowDurationMs = Math.max(0, latestPoint.trustedAt - anchor.trustedAt)
    if (!this.suspended && !this.awaitingResumeBaseline && now - this.latestSourceAt > this.staleMs) {
      return { value: null, status: 'unavailable', windowDurationMs, updatedAt: this.latestSourceAt }
    }
    if (this.recoveryStartedAt !== null
      || windowDurationMs < this.warmupMs
      || this.points.length < MINIMUM_RATE_POINTS) {
      const estimate = windowDurationMs >= LIVE_RATE_ESTIMATE_WARMUP_MS
        && this.points.length >= MINIMUM_ESTIMATE_POINTS
        ? this.trendPerHour()
        : Number.NaN
      return {
        value: Number.isFinite(estimate) ? estimate : null,
        status: 'warming',
        windowDurationMs,
        updatedAt: this.latestSourceAt,
      }
    }

    const value = this.trendPerHour()
    return {
      value: Number.isFinite(value) ? value : null,
      status: Number.isFinite(value) ? 'ready' : 'warming',
      windowDurationMs,
      updatedAt: latestPoint.sourceAt,
    }
  }

  private prune(trustedAt: number): void {
    const cutoff = trustedAt - this.windowMs
    while (this.points.length > 1 && (this.points[0]?.trustedAt ?? Infinity) < cutoff) this.points.shift()
  }

  private trendPerHour(): number {
    const origin = this.points[0]?.trustedAt ?? 0
    const samples = this.points.map((point) => ({
      time: point.trustedAt - origin,
      total: point.cumulativeTotal,
    }))
    const meanTime = samples.reduce((sum, point) => sum + point.time, 0) / samples.length
    const meanTotal = samples.reduce((sum, point) => sum + point.total, 0) / samples.length
    let covariance = 0
    let variance = 0
    for (const point of samples) {
      const centeredTime = point.time - meanTime
      covariance += centeredTime * (point.total - meanTotal)
      variance += centeredTime * centeredTime
    }
    return variance > 0 ? covariance / variance * HOUR_MS : Number.NaN
  }

  private validPoint(point: TrustedRatePoint): boolean {
    return Number.isFinite(point.trustedAt)
      && point.trustedAt >= 0
      && Number.isFinite(point.cumulativeTotal)
      && Number.isFinite(point.sourceAt)
      && point.sourceAt >= 0
  }
}
